import { stateFromSnapshot } from '../game/cosmos.js';
import { paintCosmosStatic } from '../game/renderer.js';

export const SHARE_URL = 'https://orbeat.level.farm';

const GRADE_COLORS = { S: '#c7ff45', A: '#526dff', B: '#f3f4f6', C: '#858a96' };
const FONT = '"Inter", system-ui, sans-serif';
const MONO = '"Space Mono", ui-monospace, monospace';

/**
 * Composes the social share card (1200x630): the run's universe as
 * backdrop, score + grade + song on top, Orbeat link at the bottom.
 * @returns {HTMLCanvasElement}
 */
export function buildShareCard({ result, songTitle, artistName }) {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Universe backdrop.
  ctx.fillStyle = '#090a0d';
  ctx.fillRect(0, 0, W, H);
  const state = result.universe ? stateFromSnapshot(result.universe) : null;
  if (state) paintCosmosStatic(ctx, W, H, state);

  // Left gradient scrim keeps the text readable.
  const scrim = ctx.createLinearGradient(0, 0, W * 0.72, 0);
  scrim.addColorStop(0, 'rgba(9,10,13,.92)');
  scrim.addColorStop(0.55, 'rgba(9,10,13,.55)');
  scrim.addColorStop(1, 'rgba(9,10,13,0)');
  ctx.fillStyle = scrim;
  ctx.fillRect(0, 0, W, H);

  const PAD = 72;
  ctx.textBaseline = 'alphabetic';

  // Brand.
  ctx.fillStyle = '#f3f4f6';
  ctx.font = `800 44px ${FONT}`;
  ctx.fillText('ORBEAT', PAD, PAD + 36);
  ctx.fillStyle = '#858a96';
  ctx.font = `400 17px ${MONO}`;
  ctx.fillText('ONE ORBIT. ONE UNIVERSE.', PAD, PAD + 68);

  // Grade + score.
  const grade = String(result.grade || 'C').slice(0, 2);
  ctx.fillStyle = GRADE_COLORS[grade] || GRADE_COLORS.C;
  ctx.font = `800 210px ${FONT}`;
  ctx.fillText(grade, PAD - 8, 400);

  ctx.fillStyle = '#f3f4f6';
  ctx.font = `700 58px ${MONO}`;
  ctx.fillText(Math.floor(result.score || 0).toLocaleString('fr-FR'), PAD, 476);
  ctx.fillStyle = '#858a96';
  ctx.font = `400 19px ${MONO}`;
  ctx.fillText(`MAX COMBO ${Math.floor(result.maxCombo || 0)}`, PAD, 510);

  // Song.
  const title = String(songTitle || '').replace(/<[^>]*>/g, '').slice(0, 42).toUpperCase();
  ctx.fillStyle = '#f3f4f6';
  ctx.font = `700 25px ${MONO}`;
  ctx.fillText(title, PAD, 566);
  if (artistName) {
    ctx.fillStyle = '#858a96';
    ctx.font = `400 18px ${MONO}`;
    ctx.fillText(String(artistName).slice(0, 42).toUpperCase(), PAD, 594);
  }

  // Universe stamp + link, bottom right.
  ctx.textAlign = 'right';
  if (result.universe) {
    ctx.fillStyle = '#7f95ff';
    ctx.font = `700 21px ${MONO}`;
    ctx.fillText(`UNIVERSE · ${result.universe.name}`, W - PAD, 536);
    ctx.fillStyle = '#858a96';
    ctx.font = `400 17px ${MONO}`;
    ctx.fillText(result.universe.era, W - PAD, 564);
  }
  ctx.fillStyle = '#c7ff45';
  ctx.font = `700 21px ${MONO}`;
  ctx.fillText(SHARE_URL.replace('https://', ''), W - PAD, 600);
  ctx.textAlign = 'left';

  return canvas;
}

/**
 * Shares the card: native share sheet with the image when available
 * (mobile), otherwise downloads the PNG and copies the link.
 * @returns {Promise<'shared'|'downloaded'|'failed'>}
 */
export async function shareCard(canvas, { text }) {
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) return 'failed';

  const file = new File([blob], 'orbeat-score.png', { type: 'image/png' });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text, url: SHARE_URL, title: 'Orbeat' });
      return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'shared'; // user closed the sheet
      // fall through to download
    }
  }

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'orbeat-score.png';
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
  try {
    await navigator.clipboard?.writeText(`${text} ${SHARE_URL}`);
  } catch { /* clipboard optional */ }
  return 'downloaded';
}
